// Versão: 1.02
// Data..: 2026-09-26
// Página "Financeiro API" (GestãoClick) — SOMENTE ADMIN. Reúne os relatórios
// financeiros (contas a receber/pagar + projeção, vendas de serviço, notas fiscais)
// e a automação de cobrança. Não visível a atendentes.
// v1.02: miolo extraído em FinanceApiPanel (reutilizado como aba em Administração >
//        Sistema > Financeiro API); a rota /finance permanece por compatibilidade.
// v1.01: filtros em contas a receber/pagar (situação/busca/valor/ordenação);
//        envio de NFS-e por e-mail (cadastrado ou digitado); exclusões de cobrança.

import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  UserRole,
  FinanceStatus,
  FinanceStatusLabels,
  BillingChannel,
  BillingChannelLabels,
  type Company,
  type Paginated,
  type FinanceReport,
  type FinanceSalesReport,
  type FinanceInvoicesReport,
  type FinanceEntryDto,
  type FinanceInvoiceDto,
  type BillingAutomationConfig,
  type BillingRunResult,
  type BillingReminderLog,
} from '@makweb/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import { useDialog } from '../context/DialogContext.js';
import { PageHeader } from '../components/PageHeader.js';
import { Modal } from '../components/Modal.js';
import { generateFinanceReportPdf, generateSalesReportPdf, generateInvoicesReportPdf } from '../lib/financePdf.js';

// Sub-áreas do Financeiro.
type FinanceSub = 'report' | 'sales' | 'invoices' | 'automation';

const financeSubs: { id: FinanceSub; label: string; icon: string }[] = [
  { id: 'report', label: 'Contas a receber/pagar', icon: '💵' },
  { id: 'sales', label: 'Vendas (serviços)', icon: '📊' },
  { id: 'invoices', label: 'Notas fiscais', icon: '🧾' },
  { id: 'automation', label: 'Automação de cobrança', icon: '🔔' },
];

// Formata moeda BRL.
const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
// Formata AAAA-MM-DD como dd/mm/aaaa (sem fuso).
const dateBr = (iso?: string) => {
  if (!iso) return '-';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

// Miolo do Financeiro API: navegação das sub-áreas + views. Sem PageHeader/full-bleed,
// para ser reutilizado como aba dentro da Administração (grupo Sistema).
export function FinanceApiPanel() {
  const [sub, setSub] = useState<FinanceSub>('report');
  return (
    <div>
      {/* Navegação das sub-áreas do Financeiro. */}
      <div className="flex gap-2 flex-wrap mb-4" role="tablist">
        {financeSubs.map((s) => (
          <button
            key={s.id}
            className={`btn btn-sm ${sub === s.id ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setSub(s.id)}
            title={s.label}
          >
            <span aria-hidden="true">{s.icon}</span> {s.label}
          </button>
        ))}
      </div>
      {sub === 'report' && <FinanceReportView />}
      {sub === 'sales' && <FinanceSalesView />}
      {sub === 'invoices' && <FinanceInvoicesView />}
      {sub === 'automation' && <BillingAutomationView />}
    </div>
  );
}

// Página principal (rota /finance): guarda de admin + cabeçalho + o painel.
// Mantida por compatibilidade; a navegação principal agora abre pela aba de
// Administração > Sistema > Financeiro API.
export function FinancePage() {
  const { user } = useAuth();

  // Segurança de UI: só admin acessa (o backend também exige admin em cada rota).
  if (user && user.role !== UserRole.ADMIN) return <Navigate to="/" replace />;

  return (
    <div className="full-bleed">
      <PageHeader eyebrow="Gestão" title="Financeiro API" description="Relatórios financeiros da GestãoClick e automação de cobrança." />
      <FinanceApiPanel />
    </div>
  );
}

// Cartão de KPI com dica ao passar o mouse (tooltip via atributo title).
function FinanceKpi({ icon, label, value, hint, tone }: { icon: string; label: string; value: string; hint?: string; tone?: 'ok' | 'warn' | 'bad' }) {
  const color = tone === 'bad' ? 'var(--danger)' : tone === 'warn' ? 'var(--warning)' : tone === 'ok' ? 'var(--success)' : undefined;
  return (
    <div className="stat-card" title={hint}>
      <span className="stat-icon">{icon}</span>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}

// Filtros comuns de empresa + período (reutilizado nas sub-abas).
function FinanceFilters({ companies, company, setCompany, from, setFrom, to, setTo, onRun, loading, cachedAt, onExport, showCompany = true }: {
  companies: Company[]; company: string; setCompany: (v: string) => void;
  from: string; setFrom: (v: string) => void; to: string; setTo: (v: string) => void;
  onRun: (refresh: boolean) => void; loading: boolean; cachedAt?: string | null;
  onExport?: () => void; showCompany?: boolean;
}) {
  return (
    <div className="card">
      <div className="flex gap-4 flex-wrap items-end">
        {showCompany && (
          <div className="field mb-0" style={{ minWidth: 200 }}>
            <label className="label">Empresa</label>
            <select className="select" value={company} onChange={(e) => setCompany(e.target.value)}>
              <option value="">Todas</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
        <div className="field mb-0"><label className="label">De</label><input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div className="field mb-0"><label className="label">Até</label><input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <button className="btn btn-primary" onClick={() => onRun(false)} disabled={loading} title="Consulta usando o cache quando disponível (evita refazer a chamada à API)">{loading ? 'Consultando...' : 'Consultar'}</button>
        <button className="btn btn-ghost" onClick={() => onRun(true)} disabled={loading} title="Ignora o cache e busca dados novos na GestãoClick">↻ Atualizar</button>
        {onExport && <button className="btn btn-ghost" onClick={onExport} disabled={loading} title="Exportar este relatório em PDF">⬇ PDF</button>}
      </div>
      {cachedAt && <div className="text-dim text-xs mt-2" title="Dados servidos do histórico local; use Atualizar para renovar">📦 Dados de {new Date(cachedAt).toLocaleString('pt-BR')} (cache).</div>}
    </div>
  );
}

// ---- Sub-aba: Contas a receber / a pagar + projeção ----
function FinanceReportView() {
  const toast = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [report, setReport] = useState<FinanceReport | null>(null);
  const [loading, setLoading] = useState(false);
  const firstOfMonth = new Date(); firstOfMonth.setDate(1);
  const [company, setCompany] = useState('');
  const [from, setFrom] = useState(firstOfMonth.toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [view, setView] = useState<'receivables' | 'payables'>('receivables');

  // Filtros aplicados no cliente (sobre os dados já carregados; não refazem GET).
  const [statusFilter, setStatusFilter] = useState<'all' | FinanceStatus>('all');
  const [search, setSearch] = useState(''); // cliente/descrição
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [sortBy, setSortBy] = useState<'dueDate' | 'amount' | 'daysOverdue'>('dueDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => { api.get<Paginated<Company>>('/companies').then((r) => setCompanies(r.items)).catch(() => {}); }, []);

  const run = (refresh: boolean) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (company) params.set('company', company);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (refresh) params.set('refresh', 'true');
    api.get<FinanceReport>(`/finance/report?${params.toString()}`)
      .then(setReport)
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao consultar o financeiro'))
      .finally(() => setLoading(false));
  };
  useEffect(() => run(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const side = report ? report[view] : null;
  const statusBadge = (s: FinanceStatus) => {
    const cls = s === FinanceStatus.OVERDUE ? 'badge-high' : s === FinanceStatus.PAID ? 'badge-low' : 'badge-normal';
    return <span className={`badge ${cls}`} title={`Situação: ${FinanceStatusLabels[s]}`}>{FinanceStatusLabels[s]}</span>;
  };

  // Aplica os filtros e a ordenação sobre os lançamentos da visão atual.
  const filteredEntries = (() => {
    if (!side) return [] as FinanceEntryDto[];
    const term = search.trim().toLowerCase();
    const min = minValue ? Number(minValue.replace(',', '.')) : undefined;
    const max = maxValue ? Number(maxValue.replace(',', '.')) : undefined;
    let list = side.entries.filter((e) => {
      if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (term) {
        const hay = `${e.companyName ?? ''} ${e.clientName ?? ''} ${e.description ?? ''} ${e.code ?? ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (min !== undefined && e.amount < min) return false;
      if (max !== undefined && e.amount > max) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      if (sortBy === 'amount') return (a.amount - b.amount) * dir;
      if (sortBy === 'daysOverdue') return (a.daysOverdue - b.daysOverdue) * dir;
      return (a.dueDate ?? '').localeCompare(b.dueDate ?? '') * dir; // dueDate
    });
    return list;
  })();

  // Soma dos valores filtrados (para o rodapé/contador).
  const filteredTotal = filteredEntries.reduce((s, e) => s + e.amount, 0);

  // Exporta em PDF respeitando os filtros atuais (clona o report com as entradas filtradas).
  const exportFiltered = () => {
    if (!report) return;
    const clone: FinanceReport = {
      ...report,
      [view]: { ...report[view], entries: filteredEntries },
    };
    generateFinanceReportPdf(clone);
  };

  const clearFilters = () => {
    setStatusFilter('all'); setSearch(''); setMinValue(''); setMaxValue('');
    setSortBy('dueDate'); setSortDir('asc');
  };

  return (
    <div>
      <FinanceFilters
        companies={companies} company={company} setCompany={setCompany}
        from={from} setFrom={setFrom} to={to} setTo={setTo}
        onRun={run} loading={loading} cachedAt={report?.fromCache ? report.generatedAt : null}
        onExport={report ? exportFiltered : undefined}
      />

      {report && (
        <div className="grid mt-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--s4)' }}>
          <FinanceKpi icon="🟡" label="A receber (aberto)" value={brl(report.receivables.totals.openAmount)} hint={`${report.receivables.totals.openCount} lançamento(s)`} />
          <FinanceKpi icon="🔴" label="A receber (atrasado)" value={brl(report.receivables.totals.overdueAmount)} tone="bad" hint={`${report.receivables.totals.overdueCount} lançamento(s) vencido(s)`} />
          <FinanceKpi icon="🟢" label="Recebido" value={brl(report.receivables.totals.paidAmount)} tone="ok" hint={`${report.receivables.totals.paidCount} confirmado(s)`} />
          <FinanceKpi icon="🟠" label="A pagar (atrasado)" value={brl(report.payables.totals.overdueAmount)} tone="warn" hint={`${report.payables.totals.overdueCount} conta(s) vencida(s)`} />
        </div>
      )}

      {report && report.projection.length > 0 && (
        <div className="card mt-4">
          <h3 title="Recebimentos em aberto agrupados pelo mês de vencimento">📅 Projeção de recebimentos</h3>
          <div className="flex flex-col gap-2 mt-2">
            {report.projection.map((p) => {
              const max = Math.max(...report.projection.map((x) => x.amount), 1);
              return (
                <div key={p.month} className="flex items-center gap-3" title={`${p.count} lançamento(s) vencendo em ${p.month}`}>
                  <span style={{ width: 80 }} className="text-sm">{p.month}</span>
                  <div style={{ flex: 1, background: 'var(--surface-2)', borderRadius: 6, overflow: 'hidden', height: 18 }}>
                    <div style={{ width: `${(p.amount / max) * 100}%`, background: 'var(--brand)', height: '100%' }} />
                  </div>
                  <span className="text-sm font-semibold" style={{ width: 120, textAlign: 'right' }}>{brl(p.amount)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-4 mb-2">
        <button className={`btn btn-sm ${view === 'receivables' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('receivables')}>A receber</button>
        <button className={`btn btn-sm ${view === 'payables' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('payables')}>A pagar</button>
      </div>

      {/* Filtros da lista (aplicados sobre os dados já carregados, sem refazer GET). */}
      <div className="card mb-2">
        <div className="flex gap-3 flex-wrap items-end">
          <div className="field mb-0">
            <label className="label">Situação</label>
            <select className="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'all' | FinanceStatus)}>
              <option value="all">Todas</option>
              <option value={FinanceStatus.OVERDUE}>Em atraso</option>
              <option value={FinanceStatus.OPEN}>Em aberto</option>
              <option value={FinanceStatus.PAID}>Confirmado</option>
            </select>
          </div>
          <div className="field mb-0" style={{ minWidth: 200 }}>
            <label className="label">Buscar (cliente/descrição)</label>
            <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Digite para filtrar..." />
          </div>
          <div className="field mb-0" style={{ maxWidth: 120 }}>
            <label className="label">Valor mín.</label>
            <input className="input" type="number" min={0} value={minValue} onChange={(e) => setMinValue(e.target.value)} placeholder="0" />
          </div>
          <div className="field mb-0" style={{ maxWidth: 120 }}>
            <label className="label">Valor máx.</label>
            <input className="input" type="number" min={0} value={maxValue} onChange={(e) => setMaxValue(e.target.value)} placeholder="—" />
          </div>
          <div className="field mb-0">
            <label className="label">Ordenar por</label>
            <select className="select" value={sortBy} onChange={(e) => setSortBy(e.target.value as 'dueDate' | 'amount' | 'daysOverdue')}>
              <option value="dueDate">Vencimento</option>
              <option value="amount">Valor</option>
              <option value="daysOverdue">Dias em atraso</option>
            </select>
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            title={sortDir === 'asc' ? 'Ordem crescente' : 'Ordem decrescente'}
          >{sortDir === 'asc' ? '↑ Crescente' : '↓ Decrescente'}</button>
          {/* Atalho rápido para o caso mais comum: só clientes em atraso. */}
          <button
            className={`btn btn-sm ${statusFilter === FinanceStatus.OVERDUE ? 'btn-danger' : 'btn-ghost'}`}
            onClick={() => setStatusFilter((s) => (s === FinanceStatus.OVERDUE ? 'all' : FinanceStatus.OVERDUE))}
            title="Mostra apenas os lançamentos em atraso"
          >🔴 Somente atrasados</button>
          <button className="btn btn-ghost btn-sm" onClick={clearFilters} title="Limpar todos os filtros">Limpar</button>
        </div>
        <div className="text-dim text-xs mt-2">
          {filteredEntries.length} de {side?.entries.length ?? 0} lançamento(s) · Total filtrado: <strong>{brl(filteredTotal)}</strong>
        </div>
      </div>

      <div className="card card-flush">
        {!side || side.entries.length === 0 ? (
          <div className="empty"><div className="empty-icon">💵</div>Nenhum lançamento no período.</div>
        ) : filteredEntries.length === 0 ? (
          <div className="empty"><div className="empty-icon">🔎</div>Nenhum lançamento corresponde aos filtros.</div>
        ) : (
          <table className="table">
            <thead><tr><th>Cliente</th><th>Descrição</th><th>Vencimento</th><th>Situação</th><th>Atraso</th><th style={{ textAlign: 'right' }}>Valor</th></tr></thead>
            <tbody>
              {filteredEntries.map((e: FinanceEntryDto) => (
                <tr key={e.id} title={e.description ?? ''}>
                  <td className="font-semibold">{e.companyName ?? e.clientName ?? '—'}</td>
                  <td className="text-muted text-sm">{e.description ?? e.code ?? '—'}</td>
                  <td className="text-sm">{dateBr(e.dueDate)}</td>
                  <td>{statusBadge(e.status)}</td>
                  <td className="text-sm">{e.daysOverdue > 0 ? <span className="badge badge-high" title={`${e.daysOverdue} dia(s) em atraso`}>{e.daysOverdue}d</span> : '—'}</td>
                  <td className="font-semibold" style={{ textAlign: 'right' }}>{brl(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---- Sub-aba: Vendas de serviço (totais mensais) ----
function FinanceSalesView() {
  const toast = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [report, setReport] = useState<FinanceSalesReport | null>(null);
  const [loading, setLoading] = useState(false);
  const firstOfYear = new Date(new Date().getFullYear(), 0, 1);
  const [company, setCompany] = useState('');
  const [from, setFrom] = useState(firstOfYear.toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => { api.get<Paginated<Company>>('/companies').then((r) => setCompanies(r.items)).catch(() => {}); }, []);

  const run = (refresh: boolean) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (company) params.set('company', company);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (refresh) params.set('refresh', 'true');
    api.get<FinanceSalesReport>(`/finance/sales?${params.toString()}`)
      .then(setReport)
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao consultar vendas'))
      .finally(() => setLoading(false));
  };
  useEffect(() => run(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const maxMonth = report ? Math.max(...report.byMonth.map((m) => m.total), 1) : 1;

  return (
    <div>
      <FinanceFilters
        companies={companies} company={company} setCompany={setCompany}
        from={from} setFrom={setFrom} to={to} setTo={setTo}
        onRun={run} loading={loading} cachedAt={report?.fromCache ? report.generatedAt : null}
        onExport={report ? () => generateSalesReportPdf(report) : undefined}
      />
      {report && (
        <div className="grid mt-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--s4)' }}>
          <FinanceKpi icon="💰" label="Total do período" value={brl(report.total)} />
          <FinanceKpi icon="🧾" label="Nº de vendas" value={String(report.count)} />
          <FinanceKpi icon="📊" label="Ticket médio" value={brl(report.count ? report.total / report.count : 0)} />
        </div>
      )}
      <div className="card card-flush mt-4">
        {!report || report.byMonth.length === 0 ? (
          <div className="empty"><div className="empty-icon">📊</div>Nenhuma venda de serviço no período.</div>
        ) : (
          <table className="table">
            <thead><tr><th>Mês</th><th>Vendas</th><th style={{ width: '50%' }}>Total</th></tr></thead>
            <tbody>
              {report.byMonth.map((m) => (
                <tr key={m.month} title={`${m.count} venda(s) em ${m.month}`}>
                  <td className="font-semibold">{m.month}</td>
                  <td>{m.count}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div style={{ flex: 1, background: 'var(--surface-2)', borderRadius: 6, overflow: 'hidden', height: 16 }}>
                        <div style={{ width: `${(m.total / maxMonth) * 100}%`, background: 'var(--brand)', height: '100%' }} />
                      </div>
                      <span className="text-sm font-semibold" style={{ minWidth: 110, textAlign: 'right' }}>{brl(m.total)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---- Sub-aba: Notas fiscais de serviço ----
function FinanceInvoicesView() {
  const toast = useToast();
  const [report, setReport] = useState<FinanceInvoicesReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null); // id da nota em envio
  // Modal "Enviar para": nota alvo e e-mail digitado.
  const [emailModal, setEmailModal] = useState<FinanceInvoiceDto | null>(null);
  const [emailTo, setEmailTo] = useState('');

  const run = (refresh: boolean) => {
    setLoading(true);
    api.get<FinanceInvoicesReport>(`/finance/invoices${refresh ? '?refresh=true' : ''}`)
      .then(setReport)
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao consultar notas fiscais'))
      .finally(() => setLoading(false));
  };
  useEffect(() => run(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Envia a nota. Quando `to` vem vazio, o backend usa o e-mail cadastrado do cliente.
  const sendInvoice = (invoice: FinanceInvoiceDto, to?: string) => {
    setSendingId(invoice.id);
    api.post<{ sent: boolean; to: string }>(`/finance/invoices/${invoice.id}/send`, { to })
      .then((r) => { toast.success(`Nota enviada para ${r.to}.`); setEmailModal(null); setEmailTo(''); })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao enviar a nota'))
      .finally(() => setSendingId(null));
  };

  const openEmailModal = (invoice: FinanceInvoiceDto) => { setEmailModal(invoice); setEmailTo(''); };

  return (
    <div>
      <div className="card">
        <div className="flex gap-3 flex-wrap items-center">
          <button className="btn btn-primary" onClick={() => run(false)} disabled={loading} title="Usa o cache quando disponível">{loading ? 'Consultando...' : 'Consultar'}</button>
          <button className="btn btn-ghost" onClick={() => run(true)} disabled={loading} title="Ignora o cache e busca na GestãoClick">↻ Atualizar</button>
          <button className="btn btn-ghost" onClick={() => report && generateInvoicesReportPdf(report)} disabled={!report || loading} title="Exportar em PDF">⬇ PDF</button>
          {report?.fromCache && <span className="text-dim text-xs" title="Dados do histórico local">📦 Cache de {new Date(report.generatedAt).toLocaleString('pt-BR')}</span>}
        </div>
      </div>
      {report && (
        <div className="grid mt-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--s4)' }}>
          <FinanceKpi icon="🧾" label="Qtd. de notas" value={String(report.count)} />
          <FinanceKpi icon="💰" label="Valor total" value={brl(report.total)} />
          {Object.entries(report.byStatus).map(([s, c]) => (
            <FinanceKpi key={s} icon="📄" label={s} value={String(c)} hint={`Notas com situação "${s}"`} tone={s.includes('erro') ? 'bad' : s.includes('cancel') ? 'warn' : 'ok'} />
          ))}
        </div>
      )}
      <div className="card card-flush mt-4">
        {!report || report.invoices.length === 0 ? (
          <div className="empty"><div className="empty-icon">🧾</div>Nenhuma nota fiscal encontrada.</div>
        ) : (
          <table className="table">
            <thead><tr><th>Número</th><th>Emissão</th><th>Cliente</th><th>Situação</th><th style={{ textAlign: 'right' }}>ISS</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Envio</th></tr></thead>
            <tbody>
              {report.invoices.map((n) => (
                <tr key={n.id} title={n.description ?? ''}>
                  <td className="font-semibold">{n.number ?? '—'}</td>
                  <td className="text-sm">{dateBr(n.issueDate)}</td>
                  <td>{n.clientName ?? '—'}</td>
                  <td className="text-sm">{n.status ?? '—'}</td>
                  <td className="text-sm" style={{ textAlign: 'right' }}>{brl(n.issAmount)}</td>
                  <td className="font-semibold" style={{ textAlign: 'right' }}>{brl(n.totalAmount)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => sendInvoice(n)}
                      disabled={sendingId === n.id}
                      title="Enviar para o e-mail cadastrado do cliente"
                    >{sendingId === n.id ? '...' : '✉ Enviar'}</button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => openEmailModal(n)}
                      disabled={sendingId === n.id}
                      title="Digitar um e-mail antes de enviar"
                    >✏ Enviar para</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal "Enviar para": digita o e-mail de destino antes de enviar. */}
      <Modal
        open={!!emailModal}
        title={`Enviar nota ${emailModal?.number ?? ''} por e-mail`}
        onClose={() => { setEmailModal(null); setEmailTo(''); }}
        maxWidth={460}
        footer={
          <>
            <button className="btn btn-ghost" type="button" onClick={() => { setEmailModal(null); setEmailTo(''); }}>Cancelar</button>
            <button
              className="btn btn-primary"
              type="submit"
              form="invoice-email-form"
              disabled={!!sendingId}
            >{sendingId ? 'Enviando...' : 'Enviar'}</button>
          </>
        }
      >
        <form
          id="invoice-email-form"
          onSubmit={(e) => { e.preventDefault(); if (emailModal) sendInvoice(emailModal, emailTo.trim()); }}
        >
          <p className="text-muted text-sm mb-2">A nota será enviada para o endereço informado (não altera o cadastro).</p>
          <div className="field">
            <label className="label">E-mail de destino</label>
            <input
              className="input"
              type="email"
              required
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="cliente@empresa.com.br"
              autoFocus
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ---- Sub-aba: Automação de cobrança (config + execução + histórico) ----
function BillingAutomationView() {
  const toast = useToast();
  const { confirm } = useDialog();
  const [cfg, setCfg] = useState<BillingAutomationConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState<BillingReminderLog[]>([]);
  const [lastRun, setLastRun] = useState<BillingRunResult | null>(null);
  // Empresas (para o seletor de exclusão) e estado do formulário de exclusão.
  const [companies, setCompanies] = useState<Company[]>([]);
  const [exclCompany, setExclCompany] = useState('');
  const [exclReason, setExclReason] = useState('');

  const loadCfg = () => api.get<BillingAutomationConfig>('/finance/automation').then(setCfg).catch(() => {}).finally(() => setLoading(false));
  const loadHistory = () => api.get<Paginated<BillingReminderLog>>('/finance/automation/history').then((r) => setHistory(r.items)).catch(() => {});
  useEffect(() => { api.get<Paginated<Company>>('/companies').then((r) => setCompanies(r.items)).catch(() => {}); }, []);

  // Adiciona a empresa selecionada à lista de exclusões (não cobrar). Fixa para os próximos envios.
  const addExclusion = () => {
    if (!exclCompany) return;
    api.post<BillingAutomationConfig>('/finance/automation/exclusions', { companyId: exclCompany, reason: exclReason.trim() || undefined })
      .then((c) => { setCfg(c); setExclCompany(''); setExclReason(''); toast.success('Cliente adicionado às exclusões.'); })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao excluir cliente'));
  };

  // Remove a exclusão (a empresa volta a ser cobrada).
  const removeExclusion = (companyId: string) => {
    api.delete<BillingAutomationConfig>(`/finance/automation/exclusions/${companyId}`)
      .then((c) => { setCfg(c); toast.success('Exclusão removida.'); })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao remover exclusão'));
  };

  // Prévia de um envio do histórico (mostra o conteúdo e permite ver o boleto).
  const [preview, setPreview] = useState<BillingReminderLog | null>(null);
  const [boletoLoading, setBoletoLoading] = useState(false);
  const verBoleto = (codigo?: string) => {
    if (!codigo) return;
    setBoletoLoading(true);
    api.get<{ pdfBase64: string }>(`/finance/automation/boleto/${codigo}`)
      .then((r) => {
        // Abre o PDF numa nova aba a partir do base64.
        const bin = atob(r.pdfBase64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao carregar o boleto'))
      .finally(() => setBoletoLoading(false));
  };

  // Cobrança de teste: envia para um e-mail digitado, buscando o boleto pelo CNPJ.
  const [testTo, setTestTo] = useState('');
  const [testCnpj, setTestCnpj] = useState('');
  const [testing, setTesting] = useState(false);
  const sendTest = () => {
    if (!testTo.trim() || !testCnpj.trim()) { toast.error('Informe e-mail e CNPJ.'); return; }
    setTesting(true);
    api.post<{ sent: boolean; to: string; boletoEncontrado: boolean; situacao?: string }>('/finance/billing/test', { to: testTo.trim(), cnpj: testCnpj.trim() })
      .then((r) => {
        toast.success(
          r.boletoEncontrado
            ? `Cobrança de teste enviada para ${r.to} com boleto anexo (situação: ${r.situacao ?? '—'}).`
            : `E-mail enviado para ${r.to}, mas nenhum boleto foi localizado para esse CNPJ no Inter.`,
        );
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao enviar teste'))
      .finally(() => setTesting(false));
  };

  useEffect(() => { loadCfg(); loadHistory(); }, []);

  const set = <K extends keyof BillingAutomationConfig>(k: K, v: BillingAutomationConfig[K]) => setCfg((c) => (c ? { ...c, [k]: v } : c));
  const toggleChannel = (ch: BillingChannel) => {
    if (!cfg) return;
    const has = cfg.channels.includes(ch);
    set('channels', (has ? cfg.channels.filter((c) => c !== ch) : [...cfg.channels, ch]) as BillingChannel[]);
  };

  const save = () => {
    if (!cfg) return;
    setSaving(true);
    api.put<BillingAutomationConfig>('/finance/automation', cfg)
      .then((c) => { setCfg(c); toast.success('Configuração salva.'); })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao salvar'))
      .finally(() => setSaving(false));
  };

  const runNow = async () => {
    const ok = await confirm({
      title: 'Executar cobrança agora?',
      message: 'Isto enviará avisos de cobrança reais (WhatsApp/e-mail) para os clientes com débitos em atraso elegíveis. Deseja continuar?',
      confirmLabel: 'Executar', danger: true,
    });
    if (!ok) return;
    setRunning(true);
    api.post<BillingRunResult>('/finance/automation/run', {})
      .then((r) => { setLastRun(r); loadHistory(); toast.success(`Cobrança executada: ${r.sent} envio(s).`); })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao executar'))
      .finally(() => setRunning(false));
  };

  if (loading || !cfg) return <div className="flex justify-center" style={{ padding: 40 }}><span className="spinner" /></div>;

  return (
    <div>
      <div className="card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 title="Envia lembretes de cobrança para clientes com débitos em atraso">🔔 Automação de cobrança</h3>
          <label className="flex items-center gap-2" style={{ cursor: 'pointer' }} title="Liga/desliga o disparo automático diário">
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            {cfg.enabled ? 'Ativa' : 'Desativada'}
          </label>
        </div>
        <p className="text-muted text-sm mt-1">
          Quando ativa, o portal envia lembretes 1x/dia (dias úteis) aos clientes com contas a receber em atraso.
          Estes dados e envios são visíveis apenas para administradores.
        </p>

        <div className="form-grid mt-3">
          <div className="field">
            <label className="label" title="Só cobra lançamentos com pelo menos este nº de dias de atraso">Atraso mínimo (dias)</label>
            <input className="input" type="number" min={0} value={cfg.minDaysOverdue} onChange={(e) => set('minDaysOverdue', Number(e.target.value))} />
          </div>
          <div className="field">
            <label className="label" title="Nº máximo de lembretes enviados por lançamento">Máx. lembretes por conta</label>
            <input className="input" type="number" min={1} value={cfg.maxRemindersPerEntry} onChange={(e) => set('maxRemindersPerEntry', Number(e.target.value))} />
          </div>
          <div className="field">
            <label className="label" title="Intervalo mínimo entre dois lembretes da mesma conta">Intervalo entre lembretes (dias)</label>
            <input className="input" type="number" min={0} value={cfg.reminderIntervalDays} onChange={(e) => set('reminderIntervalDays', Number(e.target.value))} />
          </div>
          <div className="field">
            <label className="label" title="Assunto do e-mail de cobrança">Assunto do e-mail</label>
            <input className="input" value={cfg.emailSubject} onChange={(e) => set('emailSubject', e.target.value)} />
          </div>
          <div className="field">
            <label className="label" title="Recebe uma cópia oculta (BCC) de toda cobrança enviada por e-mail">E-mail em cópia (BCC)</label>
            <input className="input" type="email" value={cfg.copyEmail ?? ''} onChange={(e) => set('copyEmail', e.target.value)} placeholder="financeiro@makweb.com.br (opcional)" />
          </div>
          <div className="field">
            <label className="label" title="Considera débitos vencidos a partir desta data (para alcançar devedores antigos). Vazio = sem limite inicial.">Vencimento a partir de</label>
            <input className="input" type="date" value={cfg.dueDateFrom ?? ''} onChange={(e) => set('dueDateFrom', e.target.value)} />
            <span className="text-dim text-xs">A data final é sempre hoje (automática).</span>
          </div>
        </div>

        <div className="field mt-2">
          <label className="label">Canais de envio</label>
          <div className="flex gap-4">
            {(Object.values(BillingChannel) as BillingChannel[]).map((ch) => (
              <label key={ch} className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={cfg.channels.includes(ch)} onChange={() => toggleChannel(ch)} />
                {BillingChannelLabels[ch]}
              </label>
            ))}
          </div>
        </div>

        <div className="field mt-2">
          <label className="label" title="Placeholders: {cliente} {valor} {vencimento} {dias} {codigo}">Mensagem</label>
          <textarea className="input" rows={4} value={cfg.messageTemplate} onChange={(e) => set('messageTemplate', e.target.value)} />
          <span className="text-dim text-xs">Variáveis: {'{cliente}'} {'{valor}'} {'{vencimento}'} {'{dias}'} {'{codigo}'}</span>
        </div>

        <div className="flex gap-2 mt-3">
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar configuração'}</button>
          <button className="btn btn-ghost" onClick={runNow} disabled={running} title="Dispara a cobrança imediatamente para as contas elegíveis">{running ? 'Executando...' : '▶ Executar agora'}</button>
        </div>

        {lastRun && (
          <div className="text-sm mt-3" style={{ padding: 10, background: 'var(--surface-2)', borderRadius: 8 }}>
            Última execução: {lastRun.sent} enviado(s), {lastRun.failed} falha(s), {lastRun.skipped} ignorado(s) — de {lastRun.scanned} atrasado(s).
          </div>
        )}
      </div>

      {/* Cobrança de teste: valida o envio (com boleto do Inter) para um e-mail próprio. */}
      <div className="card mt-4">
        <h3 title="Envia um e-mail de teste com o boleto do CNPJ informado (busca no Inter)">🧪 Cobrança de teste</h3>
        <p className="text-muted text-sm mt-1">
          Envia um modelo de cobrança para o e-mail informado, buscando o boleto do cliente no Banco Inter
          pelo CNPJ (anexa o PDF + linha digitável/PIX). Use para validar antes de ativar o automático.
        </p>
        <div className="flex gap-3 flex-wrap items-end mt-2">
          <div className="field mb-0" style={{ minWidth: 220 }}>
            <label className="label">E-mail de destino</label>
            <input className="input" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="voce@makweb.com.br" />
          </div>
          <div className="field mb-0" style={{ minWidth: 180 }}>
            <label className="label">CNPJ do cliente</label>
            <input className="input" value={testCnpj} onChange={(e) => setTestCnpj(e.target.value)} placeholder="somente dígitos" inputMode="numeric" />
          </div>
          <button className="btn btn-primary btn-sm" onClick={sendTest} disabled={testing}>
            {testing ? 'Enviando...' : '✉ Enviar cobrança de teste'}
          </button>
        </div>
      </div>

      {/* Exclusões: clientes que nunca são cobrados automaticamente (fixado). */}
      <div className="card mt-4">
        <h3 title="Clientes marcados aqui são ignorados em todas as cobranças (manual e agendada)">🚫 Clientes excluídos da cobrança</h3>
        <p className="text-muted text-sm mt-1">
          Empresas nesta lista nunca recebem cobrança automática. A exclusão fica fixada para todos os próximos envios até ser removida.
        </p>
        <div className="flex gap-3 flex-wrap items-end mt-2">
          <div className="field mb-0" style={{ minWidth: 220 }}>
            <label className="label">Empresa</label>
            <select className="select" value={exclCompany} onChange={(e) => setExclCompany(e.target.value)}>
              <option value="">Selecione...</option>
              {companies
                .filter((c) => !(cfg.exclusions ?? []).some((x) => x.companyId === c.id))
                .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field mb-0" style={{ minWidth: 200 }}>
            <label className="label">Motivo (opcional)</label>
            <input className="input" value={exclReason} onChange={(e) => setExclReason(e.target.value)} placeholder="Ex.: acordo de pagamento" />
          </div>
          <button className="btn btn-primary btn-sm" onClick={addExclusion} disabled={!exclCompany}>Adicionar exclusão</button>
        </div>

        {(cfg.exclusions ?? []).length === 0 ? (
          <div className="text-dim text-sm mt-3">Nenhum cliente excluído. Todos os clientes com débito em atraso podem ser cobrados.</div>
        ) : (
          <table className="table mt-3">
            <thead><tr><th>Empresa</th><th>Motivo</th><th>Excluído em</th><th style={{ textAlign: 'right' }}>Ação</th></tr></thead>
            <tbody>
              {(cfg.exclusions ?? []).map((x) => (
                <tr key={x.companyId}>
                  <td className="font-semibold">{x.companyName ?? x.companyId}</td>
                  <td className="text-muted text-sm">{x.reason ?? '—'}</td>
                  <td className="text-sm">{new Date(x.addedAt).toLocaleDateString('pt-BR')}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => removeExclusion(x.companyId)} title="Remover (a empresa volta a ser cobrada)">Remover</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card card-flush mt-4">
        <div className="admin-section-head" style={{ padding: 'var(--s3) var(--s4)' }}>
          <span className="sec-icon">📜</span>
          <div><h2 style={{ fontSize: 15 }}>Histórico de envios</h2></div>
        </div>
        {history.length === 0 ? (
          <div className="empty"><div className="empty-icon">📭</div>Nenhum envio registrado.</div>
        ) : (
          <table className="table">
            <thead><tr><th>Data</th><th>Cliente</th><th>Canal</th><th>Destino</th><th style={{ textAlign: 'right' }}>Valor</th><th>Status</th><th style={{ textAlign: 'right' }}>Prévia</th></tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} title={h.error ?? ''}>
                  <td className="text-sm">{new Date(h.sentAt).toLocaleString('pt-BR')}</td>
                  <td>{h.clientName ?? '—'}</td>
                  <td>{BillingChannelLabels[h.channel] ?? h.channel}</td>
                  <td className="text-sm text-muted">{h.to}</td>
                  <td className="font-semibold" style={{ textAlign: 'right' }}>{brl(h.amount)}</td>
                  <td>{h.success ? <span className="badge badge-low" title="Enviado">Enviado</span> : <span className="badge badge-high" title={h.error ?? 'Falha'}>Falha</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setPreview(h)} title="Ver o que foi enviado">👁 Ver</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de prévia do envio (conteúdo + boleto). */}
      <Modal
        open={!!preview}
        title={`Envio ${preview ? BillingChannelLabels[preview.channel] ?? preview.channel : ''} — ${preview?.clientName ?? ''}`}
        onClose={() => setPreview(null)}
        maxWidth={560}
        footer={<button className="btn btn-ghost" type="button" onClick={() => setPreview(null)}>Fechar</button>}
      >
        {preview && (
          <div className="flex flex-col gap-2">
            <div className="text-sm"><strong>Data:</strong> {new Date(preview.sentAt).toLocaleString('pt-BR')}</div>
            <div className="text-sm"><strong>Canal:</strong> {BillingChannelLabels[preview.channel] ?? preview.channel}</div>
            <div className="text-sm"><strong>Destino:</strong> {preview.to}</div>
            <div className="text-sm"><strong>Valor:</strong> {brl(preview.amount)}</div>
            {preview.subject && <div className="text-sm"><strong>Assunto:</strong> {preview.subject}</div>}
            <div className="text-sm"><strong>Status:</strong> {preview.success ? 'Enviado' : `Falha — ${preview.error ?? ''}`}</div>
            <div>
              <div className="label mt-2">Mensagem enviada</div>
              <div style={{ whiteSpace: 'pre-wrap', background: 'var(--surface-2)', borderRadius: 8, padding: 10, fontSize: 13 }}>
                {preview.body || '—'}
              </div>
            </div>
            {preview.boletoLinhaDigitavel && (
              <div className="text-sm"><strong>Linha digitável:</strong> <span style={{ fontFamily: 'monospace' }}>{preview.boletoLinhaDigitavel}</span></div>
            )}
            {preview.boletoCodigoSolicitacao && (
              <button className="btn btn-primary btn-sm mt-2" style={{ alignSelf: 'flex-start' }} disabled={boletoLoading} onClick={() => verBoleto(preview.boletoCodigoSolicitacao)}>
                {boletoLoading ? 'Abrindo...' : '📄 Ver boleto (PDF)'}
              </button>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
